/*
 * @Author: czj
 * @Date: 2026-06-15 18:32:57
 * @Description: 动态路由工具 — 扁平权限列表 → 嵌套路由树 + 按钮权限提取
 * @FilePath: \kp-rag-system-1\src\utils\routeHelper.js
 */

/**
 * 解析后端 view_path 为懒加载组件
 * view_path 格式示例：
 *   - "views/UserManagement/index.vue"
 *   - "../views/permission/dictionaries/index.vue"
 *   - "layout" / "layout.vue" → 返回 undefined（包裹路由）
 * @param {string} viewPath - 后端返回的组件路径
 * @returns {Function|undefined} Vue 异步组件或 undefined
 */
function resolveViewComponent(viewPath) {
  if (!viewPath) return undefined

  // 去除 ../ 和 ./ 前缀
  let clean = viewPath.replace(/^(\.\.\/)+/, '').replace(/^(\.\/)+/, '')

  // layout 特殊处理 → 包裹路由，不需要 component
  if (clean === 'layout' || clean === 'layout.vue') {
    return undefined
  }

  // 确保 .vue 后缀（后端可能带也可能不带）
  if (!clean.endsWith('.vue')) {
    clean = clean + '.vue'
  }

  // Webpack 动态 import — 使用 @ 别名 + 模板字符串
  // Webpack 会在构建时生成 context module 以支持此动态表达式
  // return () => import(`@/${clean}`)
  return () => import(/* webpackInclude: /\.vue$/ */ `@/${clean}`)
}

/**
 * 扁平列表 → 树状结构（供 Vue Router 使用）
 * @param {Array} list - 扁平权限列表，每项含 id/father_id/path/name/name_zh/view_path 等
 * @param {number} rootValue - 根节点的 father_id 值（通常为 -1）
 * @returns {Array} 嵌套路由树
 */
export function transListToTreeData(list, rootValue) {
  const arr = []
  list.forEach(item => {
    if (item.father_id === rootValue) {
      const element = {
        id: item.id,
        father_id: item.father_id,
        is_show: item.is_show,
        hidden: !item.is_show, // Vue Router hidden 控制菜单显隐
        path: item.path,
        name: item.name,
        position: item.position,
        meta: {
          title: item.name_zh,
          icon: item.icon,
          external_link: item.external_link,
          is_open_tag: item.is_open_tag,
        },
      }

      // 懒加载组件
      const comp = resolveViewComponent(item.view_path)
      if (comp) {
        element.component = comp
      }

      // 递归获取子节点
      const children = transListToTreeData(list, item.id)
      if (children.length > 0) {
        // 自动重定向到第一个子路由
        element.redirect = `${item.path === '/' ? '' : item.path}/${children[0].path}`
        element.children = children
      }
      arr.push(element)
    }
  })
  return arr
}

/**
 * 扁平列表 → 路径映射，形如 { '/path': { is_public: true } }
 * @param {Array} list - 扁平权限列表
 * @param {number} rootValue - 根节点 father_id
 * @param {string} path - 当前路径前缀（递归用）
 * @param {Object} arr - 累积结果
 * @returns {Object} 路径 → 权限信息映射
 */
export function transListToTreePath(list, rootValue, path = '', arr = {}) {
  list.forEach(item => {
    if (item.father_id === rootValue) {
      const currentPath = (path + '/' + item.path).replace('//', '/')
      arr[currentPath] = { is_public: item.is_public }
      transListToTreePath(list, item.id, currentPath, arr)
    }
  })
  return arr
}

/**
 * 提取有权限的按钮名称列表
 * @param {Array} btns - 权限按钮列表，每项含 name/isPermission
 * @returns {string[]} 有权限的按钮名称数组
 */
export function setBtnPermission(btns) {
  if (!btns) return []
  return btns.filter(btn => btn.isPermission).map(btn => btn.name)
}

/**
 * 检查路由名称是否在角色菜单权限列表中
 * @param {Array} roleMenu - 角色菜单列表
 * @param {string} routerName - 待检查的路由名称
 * @returns {boolean}
 */
export function checkMenu(roleMenu, routerName) {
  const upperName = routerName.toUpperCase()
  for (let i = 0; i < roleMenu.length; i++) {
    if (roleMenu[i].name.toUpperCase() === upperName) {
      return roleMenu[i].isPermission
    }
  }
  return false
}

/**
 * 递归过滤异步路由（按角色菜单权限）
 * @param {Array} routes - 待过滤的路由树
 * @param {Array} roleMenu - 角色菜单权限列表
 * @returns {Array} 过滤后的路由树
 */
export function filterAsyncRoutes(routes, roleMenu) {
  const res = []
  routes.forEach(route => {
    const tmp = { ...route }
    if (tmp.notPermission) {
      res.push(tmp)
    } else {
      if (checkMenu(roleMenu, tmp.name)) {
        if (tmp.children) {
          tmp.is_show = false
          tmp.children = filterAsyncRoutes(tmp.children, roleMenu)
        }
        res.push(tmp)
      }
    }
  })
  return res
}
